import {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {diApiNa} from '@di-shared/api/di';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface HistoryDriverSelectBottomSheetData {
  vin: string;
  driverNames: Array<string>;
}

interface DriverAreaProps {
  vin: string;
  selectedDriverNames: Array<string>;
  onChangeDriverNames(driverNames: Array<string>): void;
}

export default function HistoryDriverSelectBottomSheet() {
  const initial = (window as any).data as HistoryDriverSelectBottomSheetData;
  const {t} = useInferableTranslation();
  const [selectedDriverNames, setSelectedDriverNames] = useState<Array<string>>(initial.driverNames);

  return (
    <div
      className="bottom_sheet tag_select"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bottomSheetTitle"
    >
      <div className="bottom_sheet_header">
        <h2
          id="bottomSheetTitle"
          className="bottom_sheet_title"
        >
          Driver
        </h2>
        <button
          type="button"
          className="btn btn_ic"
          aria-label={t('ctn_home_close')}
          onClick={() => window.bottomSheetClose()}
        >
          <span
            className="ic ic_close_20px"
            aria-hidden="true"
          />
        </button>
      </div>
      <div className="bottom_sheet_inner">
        <div className="bottom_sheet_contents">
          <ErrorBoundary fallback={<DriverAreaErrorFallback />}>
            <Suspense fallback={<DriverAreaLoading />}>
              <DriverArea
                vin={initial.vin}
                selectedDriverNames={selectedDriverNames}
                onChangeDriverNames={setSelectedDriverNames}
              />
            </Suspense>
          </ErrorBoundary>
        </div>
        <div className="bottom_sheet_footer">
          <button
            type="button"
            className="btn btn_large btn_filled"
            onClick={() => window.bottomSheetClose(selectedDriverNames)}
          >
            {t('ctn_mytrips_filter_apply_eu')}
          </button>
        </div>
      </div>
    </div>
  );
}

function DriverArea({vin, selectedDriverNames, onChangeDriverNames}: DriverAreaProps) {
  const {t} = useInferableTranslation();

  const {data: drivers} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.vehicle.retrieveDriverUser1), vin],
    queryFn: () => diApiNa.vehicle.retrieveDriverUser1({vin}),
    select: ({data}) => data,
  });

  const isAllDriversSelected = selectedDriverNames.length === 0;

  function toggleDriverSelection(driverName: string) {
    if (selectedDriverNames.length === 0) {
      onChangeDriverNames([driverName]);
      return;
    }

    if (selectedDriverNames.includes(driverName)) {
      const nextNames = selectedDriverNames.filter((selectedName) => selectedName !== driverName);

      onChangeDriverNames(nextNames.length > 0 ? nextNames : []);
      return;
    }

    onChangeDriverNames([...selectedDriverNames, driverName]);
  }

  return (
    <div className="tag_area">
      <button
        type="button"
        className={clsx('btn btn_round', {'is-selected': isAllDriversSelected})}
        onClick={() => onChangeDriverNames([])}
      >
        <span
          className="ic ic_check_16px"
          aria-hidden="true"
        />
        {t('ctn_mytrips_download_tags_all_eu')}
      </button>
      {drivers.map(({userId, driverName}) => (
        <button
          key={userId}
          type="button"
          className={clsx('btn btn_round', {'is-selected': selectedDriverNames.includes(driverName)})}
          onClick={() => toggleDriverSelection(driverName)}
        >
          <span
            className="ic ic_check_16px"
            aria-hidden="true"
          />
          {driverName}
        </button>
      ))}
    </div>
  );
}

function DriverAreaLoading() {
  return (
    <div className="tag_area">
      {Array.from({length: 3}).map((_, index) => (
        <span
          key={`driver-select-skeleton-${index}`}
          className="btn btn_round skeleton"
          style={{width: 72, height: 32}}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function DriverAreaErrorFallback() {
  const {t} = useInferableTranslation();

  // FIXME: Driver 전용 i18n 키 없음(신규 키 등록 전까지 하드코딩, HistoryDownloadForm과 동일)
  return (
    <div className="trip_info">
      <div className="empty_area">
        <span className="ic ic_caution_32px"></span>
        <span
          className="body_small_r"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {'Failed to load drivers.\n' + t('msg_mytrips_details_try_again_eu')}
        </span>
      </div>
    </div>
  );
}
